import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as adminService from "@/shared/api/services/adminService";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, } from "recharts";
import {
  ArrowLeft, Pencil, Building2, TrendingUp, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, XCircle, Clock, Activity, Key, Eye,
  EyeOff, Copy, Check, Percent, Webhook, Search, ChevronLeft, ChevronRight, AlertTriangle
} from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import DrillDownBreadcrumb from "@/shared/components/admin/DrillDownBreadcrumb";

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30", label: "Active" },
  suspended: { icon: AlertTriangle, color: "bg-red-500/20 text-red-400 border border-red-500/30", label: "Suspended" },
  pending: { icon: Clock, color: "bg-amber-500/20 text-amber-400 border border-amber-500/30", label: "Pending" },
  terminated: { icon: XCircle, color: "bg-slate-500/20 text-slate-400 border border-slate-500/30", label: "Terminated" },
};

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ backgroundColor: "#0a1628", border: "1px solid #22d3ee44", borderRadius: 12, padding: "10px 14px", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
      <p style={{ color: "#64748b", fontSize: 11, marginBottom: 6 }}>{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color, fontSize: 13, fontWeight: 600, margin: "2px 0" }}>
          {entry.name}: {entry.value.toFixed(1)}K
        </p>
      ))}
    </div>
  );
};

export default function AdminMerchantDetail() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const merchantId = urlParams.get("id");

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [txFilters, setTxFilters] = useState({ search: "", status: "all", type: "all" });
  const [overviewPage, setOverviewPage] = useState(0);
  const [depositsPage, setDepositsPage] = useState(0);
  const [withdrawalsPage, setWithdrawalsPage] = useState(0);
  const pageSize = 10;

  const { data: merchant } = useQuery({
    queryKey: ["merchant-detail", merchantId],
    queryFn: async () => adminService.getMerchantById(merchantId),
    enabled: !!merchantId,
  });

  const { data: stats = {} } = useQuery({
    queryKey: ['merchant-stats', merchantId],
    queryFn: async () => adminService.getMerchantDashboardLiveMetrics(merchantId),
    enabled: !!merchantId,
  });

  const merchantStatusKey = merchant?.status?.toString().toLowerCase() || "pending";
  const status = statusConfig[merchantStatusKey] || statusConfig.pending;
  const StatusIcon = status.icon;


  const copyKey = (key, type) => {
    if (!key) return;
    navigator.clipboard.writeText(key);
    setCopiedKey(type);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  /* =========================================================================
     COMBINED TRANSACTIONS (Overview Tab)
  ========================================================================= */
  const { data: combinedQRCodeAndWitData, isLoading: isLoadingQRAndWithdrawals, error: combinedError } = useQuery({
    queryKey: ["combined-transactions", overviewPage, pageSize, merchantId],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      overviewPage + 1,
      pageSize,
      "",
      null,
      [merchantId]
    ),
  });

  const combinedDataWithdrawalsAndQRCodes = combinedQRCodeAndWitData?.data || [];
  const totalCombinedPages = combinedQRCodeAndWitData?.totalPages || 0;

  const combinedData = React.useMemo(() => {
    return combinedDataWithdrawalsAndQRCodes.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id,
      type: t.type?.toLowerCase() === 'sale' ? 'deposit' : (t.type?.toLowerCase() || "unknown"),
      status: (t.status || "").toLowerCase(),
      amount: t.amount,
      net_amount: Number(t.net_amount ?? 0),
      total_fee: Number(t.total_fee ?? 0),
      provider_display: t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      merchant: t.merchant || {
        name: t.merchant_name,
        id: t.merchant_id,
        code: t.merchant_code,
      },
      reseller: t.reseller || {
        name: t.reseller_name,
        id: t.reseller_id,
        code: t.reseller_code,
      },
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
      is_qr_code: t.type?.toLowerCase() === 'sale' || t.type?.toLowerCase() === 'deposit',
    })).sort((a, b) => new Date(b.created_date || b.createdAt) - new Date(a.created_date || a.createdAt));
  }, [combinedDataWithdrawalsAndQRCodes]);

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    const dayTxs = (combinedData || []).filter(t =>
      new Date(t.created_date || t.createdAt || new Date()).toDateString() === date.toDateString() && t.status === "success");
    return {
      date: format(date, "MMM d"),
      deposits: dayTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
      withdrawals: dayTxs.filter(t => t.type === "withdrawal" || t.type === "WITHDRAWAL").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
    };
  });


  /* =========================================================================
     DEPOSIT TRANSACTIONS (Deposits Tab)
  ========================================================================= */
  const { data: qrCodesData, isLoading: qrLoading, error: qrError } = useQuery({
    queryKey: ["merchant-deposits-paginated", depositsPage, pageSize, txFilters.search, merchantId],
    queryFn: () => qrCodeService.getMerchantQRCodesPagination(depositsPage + 1, pageSize, txFilters.search, null, [merchantId]),
  });

  const qrCodes = qrCodesData?.data || [];
  const depositsTotalTransactions = qrCodesData?.total || 0;
  const depositsTotalPages = qrCodesData?.totalPages || 1;

  const depositTransactions = React.useMemo(() => {
    return qrCodes.map((q) => ({
      id: q.id ?? q.payment_id,
      payment_id: q.payment_id ?? "",
      transaction_id: q.payment_id || q.id,
      merchant_ref: q.reference ?? q.merchant_ref ?? "",
      reference: q.reference ?? q.merchant_ref ?? "",
      amount: Number(q.amount ?? 0),
      net_amount: Number(q.net_amount ?? 0),
      total_fee: Number(q.total_fee ?? 0),
      status: (q.status ?? "pending").toLowerCase(),
      currency: q.currency,
      merchant_id: q.merchant_id,
      merchant_code: q.merchant_code,
      merchant_name: q.merchant_name,
      merchant: q.merchant,
      reseller_id: q.reseller_id,
      reseller_code: q.reseller_code,
      reseller_name: q.reseller_name,
      reseller: q.reseller,
      provider_code: q.provider_code,
      provider_display: q.provider_code,
      client_bank_name: q.client_bank_name,
      client_name: q.client_name,
      client_bank_account: q.client_bank_account,
      expires_at: q.expires_at,
      created_date: q.createdAt || q.created_date || new Date(),
      createdAt: q.createdAt ?? "",
      updatedAt: q.updatedAt ?? "",
      type: "DEPOSIT",
      is_qr_code: true,
      _raw: q,
    }));
  }, [qrCodes]);

  /* =========================================================================
     WITHDRAWAL TRANSACTIONS (Withdrawals Tab)
  ========================================================================= */
  const { data: withdrawalsData, isLoading: withdrawalsLoadingPaginated } = useQuery({
    queryKey: ["all-withdrawals", withdrawalsPage, pageSize, txFilters.search, null, merchantId],
    queryFn: () => adminService.getWithdrawalsPagination(withdrawalsPage + 1, pageSize, txFilters.search, null, [merchantId]),
  });

  const withdrawalsPaginated = withdrawalsData?.data || [];
  const withdrawalsTotalTransactions = withdrawalsData?.total || 0;
  const withdrawalsTotalPages = withdrawalsData?.totalPages || 1;

  const withdrawalTransactions = React.useMemo(() => {
    return withdrawalsPaginated.map((w) => {
      return {
        id: w.id,
        transaction_id: w.id,
        payment_id: w.id,
        merchant_ref: w.bank_account,
        reference: "",
        amount: Number(w.amount ?? 0),
        net_amount: Number(w.net_amount ?? 0),
        total_fee: Number(w.total_fee ?? 0),
        status: (w.status ?? "pending").toLowerCase(),
        currency: "THB",
        merchant_id: w.merchant_id,
        merchant: merchant || w.merchants,
        bank_code: w.bank_code,
        bank_account: w.bank_account,
        account_name: w.account_name,
        customer_name: w.account_name,
        client_bank_name: w.bank_name || w.bank_code,
        provider_display: w.bank_name || w.bank_code,
        network: w.network,
        wallet_address: w.wallet_address,
        createdAt: w.createdAt ?? "",
        updatedAt: w.updatedAt ?? "",
        created_date: w.createdAt,
        type: "WITHDRAWAL",
        _raw: w,
      };
    });
  }, [withdrawalsPaginated, merchant]);

  const TABS = [
    { value: "overview", icon: Activity, label: t ? t("overview") : "Overview" },
    { value: "deposits", icon: ArrowDownToLine, label: t ? t("deposits") : "Deposits" },
    { value: "withdrawals", icon: ArrowUpFromLine, label: t ? t("withdrawals") : "Withdrawals" },
    { value: "settings", icon: Key, label: t ? t("apiAndSettings") : "API & Settings" },
  ];

  const statCards = [
    { title: "Today's Volume", value: `฿${Math.round(stats?.todayVolume) || 0}`, icon: TrendingUp },
    { title: "Total Volume", value: `฿${Math.round(stats?.totalVolume) || 0}`, icon: Activity },
    { title: "Success Rate", value: `${stats?.successRate || 0}%`, icon: Percent },
    { title: "Total Withdrawals", value: `฿${Math.round(stats?.totalWithdrawals) || 0}`, icon: ArrowUpFromLine },
    { title: "Total Deposits", value: `฿${Math.round(stats?.totalDeposits) || 0}`, icon: ArrowDownToLine },
  ];

  return (
    <div style={{ backgroundColor: "#060f1e", minHeight: "100vh", padding: "24px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');

        /* ── Tab Strip ── */
        .md-tab-strip {
          display: flex;
          gap: 4px;
          background: linear-gradient(135deg, #0a1628 0%, #0d1f35 100%);
          border: 1px solid #1e3a5f;
          border-radius: 16px;
          padding: 5px;
          width: fit-content;
          position: relative;
        }
        .md-tab-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 9px 18px;
          border-radius: 11px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid transparent;
          background: transparent;
          color: #4D6E90;
          cursor: pointer;
          transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          white-space: nowrap;
          overflow: hidden;
          font-family: 'Outfit', system-ui, sans-serif;
          letter-spacing: 0.01em;
        }
        .md-tab-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 11px;
          background: radial-gradient(circle at 50% 120%, rgba(34,211,238,0.18), transparent 65%);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .md-tab-btn:hover {
          color: #22d3ee;
          background: rgba(34,211,238,0.06);
          border-color: rgba(34,211,238,0.18);
          box-shadow: 0 0 16px rgba(34,211,238,0.08), inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .md-tab-btn:hover::before { opacity: 1; }
        .md-tab-btn.active {
          color: #22d3ee;
          background: rgba(34,211,238,0.12);
          border-color: rgba(34,211,238,0.35);
          box-shadow: 0 0 24px rgba(34,211,238,0.15), 0 0 8px rgba(34,211,238,0.1), inset 0 1px 0 rgba(255,255,255,0.06);
        }
        .md-tab-btn.active::before { opacity: 1; }
        .md-tab-btn.active .md-tab-icon {
          filter: drop-shadow(0 0 6px rgba(34,211,238,0.7));
        }
        .md-active-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #22d3ee;
          box-shadow: 0 0 8px rgba(34,211,238,0.8);
          animation: dotpulse 2s ease-in-out infinite;
          margin-left: 2px;
        }
        @keyframes dotpulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(34,211,238,0.8); }
          50% { opacity: 0.6; box-shadow: 0 0 4px rgba(34,211,238,0.4); }
        }
        .md-tab-glow-line {
          position: absolute;
          bottom: -1px; left: 8px; right: 8px;
          height: 2px;
          border-radius: 99px;
          background: linear-gradient(90deg, transparent, rgba(34,211,238,0.6), transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .md-tab-btn.active .md-tab-glow-line { opacity: 1; }

        /* ── Cards ── */
        .md-card { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 16px; overflow: hidden; transition: border-color 0.2s; }
        .md-card:hover { border-color: #1e4a7a; }
        .md-card-header { padding: 16px 20px; border-bottom: 1px solid #1e3a5f; display: flex; align-items: center; gap: 8px; }
        .md-card-title { font-size: 14px; font-weight: 600; color: #e2e8f0; margin: 0; }
        .md-card-content { padding: 20px; }

        /* ── Inputs & Selects ── */
        .md-input { background: #0a1628 !important; border: 1px solid #1e3a5f !important; color: #e2e8f0 !important; border-radius: 10px !important; transition: border-color 0.2s, box-shadow 0.2s !important; }
        .md-input:focus { border-color: #22d3ee !important; box-shadow: 0 0 0 3px rgba(34,211,238,0.1) !important; outline: none !important; }
        .md-input::placeholder { color: #334155 !important; }
        .md-select [data-radix-select-trigger] { background: #0a1628; border: 1px solid #1e3a5f; color: #e2e8f0; border-radius: 10px; }

        /* ── Filter Bar ── */
        .md-filter-bar { background: linear-gradient(135deg, #0a1628, #0d1f35); border: 1px solid #1e3a5f; border-radius: 14px; padding: 16px; display: flex; flex-wrap: wrap; gap: 12px; }

        /* ── Icon Buttons ── */
        .md-icon-btn { background: #0a1628; border: 1px solid #1e3a5f; border-radius: 8px; padding: 8px; color: #4d6e90; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; }
        .md-icon-btn:hover { border-color: #22d3ee; color: #22d3ee; background: rgba(34,211,238,0.06); box-shadow: 0 0 12px rgba(34,211,238,0.1); }

        /* ── Code blocks ── */
        .md-code { background: #060f1e; border: 1px solid #1e3a5f; border-radius: 10px; padding: 10px 14px; font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 12px; color: #94a3b8; word-break: break-all; display: block; letter-spacing: 0.02em; }

        /* ── Info rows ── */
        .md-info-row { padding: 11px 0; border-bottom: 1px solid rgba(30,58,95,0.6); }
        .md-info-row:last-child { border-bottom: none; padding-bottom: 0; }
        .md-info-label { font-size: 11px; color: #4d6e90; margin: 0 0 3px; text-transform: uppercase; letter-spacing: 0.05em; }
        .md-info-value { font-size: 14px; font-weight: 500; color: #e2e8f0; margin: 0; }

        /* ── Badges ── */
        .md-badge-cyan { background: rgba(34,211,238,0.1); border: 1px solid rgba(34,211,238,0.3); color: #22d3ee; padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; display: inline-flex; font-family: monospace; }

        /* ── Pagination ── */
        .md-page-btn { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 8px; padding: 6px 10px; color: #64748b; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; }
        .md-page-btn:hover:not(:disabled) { border-color: #22d3ee; color: #22d3ee; background: rgba(34,211,238,0.06); }
        .md-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ── Stat cards ── */
        .md-stat-card { background: linear-gradient(135deg, #0d1f35 0%, #0a1628 100%); border: 1px solid #1e3a5f; border-radius: 16px; padding: 16px; transition: all 0.25s ease; cursor: default; }
        .md-stat-card:hover { border-color: rgba(34,211,238,0.4); box-shadow: 0 0 24px rgba(34,211,238,0.08), inset 0 1px 0 rgba(34,211,238,0.05); transform: translateY(-2px); }
        .md-stat-icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.18); display: flex; align-items: center; justify-content: center; }

        /* ── Cyan button ── */
        .md-cyan-btn { display: inline-flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #0891b2, #06b6d4); color: #fff; border: 1px solid rgba(34,211,238,0.4); border-radius: 10px; padding: 10px 18px; font-weight: 600; font-size: 13px; cursor: pointer; box-shadow: 0 0 20px rgba(6,182,212,0.3); transition: all 0.2s; letter-spacing: 0.01em; }
        .md-cyan-btn:hover { background: linear-gradient(135deg, #0e7490, #0891b2); box-shadow: 0 0 32px rgba(6,182,212,0.5); transform: translateY(-1px); }

        /* ── Back btn ── */
        .md-back-btn { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 10px; padding: 8px; color: #4d6e90; cursor: pointer; transition: all 0.2s; display: inline-flex; }
        .md-back-btn:hover { border-color: #22d3ee; color: #22d3ee; background: rgba(34,211,238,0.06); box-shadow: 0 0 12px rgba(34,211,238,0.12); }

        /* ── Select content dark ── */
        [data-radix-select-content] { background: #0d1f35 !important; border: 1px solid #1e3a5f !important; border-radius: 12px !important; box-shadow: 0 16px 48px rgba(0,0,0,0.6) !important; }
        [data-radix-select-item]:hover { background: rgba(34,211,238,0.08) !important; color: #22d3ee !important; }
        [data-radix-select-item] { color: #94a3b8 !important; font-size: 13px !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <Link to={createPageUrl("Merchants")}>
            <button className="md-back-btn"><ArrowLeft style={{ width: 20, height: 20 }} /></button>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 32px rgba(34,211,238,0.25), 0 8px 24px rgba(0,0,0,0.3)" }}>
              <Building2 style={{ width: 30, height: 30, color: "#fff" }} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>
                  {merchant?.name || merchant?.code || (t ? t("merchant") : "Merchant")}
                </h1>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }} className={status.color}>
                  <StatusIcon style={{ width: 11, height: 11 }} />
                  {status.label}
                </span>
              </div>
              <p style={{ color: "#4d6e90", margin: "4px 0 0", fontSize: 13 }}>
                {merchant?.code}{merchant?.email && ` • ${merchant.email}`}
              </p>
            </div>
          </div>
        </div>
        <Link to={createPageUrl("Merchants") + "?edit=" + merchantId}>
          <button className="md-cyan-btn">
            <Pencil style={{ width: 15, height: 15 }} />
            {t ? t("editMerchant") : "Edit Merchant"}
          </button>
        </Link>
      </div>

      {/* ── Breadcrumb ── */}
      <div style={{ marginBottom: 24 }}>
        <DrillDownBreadcrumb engine={null} reseller={null} merchant={merchant} />
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 14, marginBottom: 28 }}>
        {statCards.map((stat, i) => (
          <motion.div key={stat.title} className="md-stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="md-stat-icon"><stat.icon style={{ width: 18, height: 18, color: "#22d3ee" }} /></div>
              <div>
                <p style={{ fontSize: 10, color: "#4d6e90", margin: 0, textTransform: "uppercase", letterSpacing: "0.05em" }}>{stat.title}</p>
                <p style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ══════════════════════════════════════════════
          BEAUTIFUL TAB SECTION
      ══════════════════════════════════════════════ */}
      <div>
        {/* Tab Strip */}
        <div className="md-tab-strip" style={{ marginBottom: 24 }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                className={`md-tab-btn ${isActive ? "active" : ""}`}
                onClick={() => setActiveTab(tab.value)}
              >
                <tab.icon className="md-tab-icon" style={{ width: 15, height: 15 }} />
                {tab.label}
                {isActive && <span className="md-active-dot" />}
                <span className="md-tab-glow-line" />
              </button>
            );
          })}
        </div>

        {/* Tab Panels */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 12, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        >

          {/* ── OVERVIEW ── */}
          {activeTab === "overview" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
              <div style={{ gridColumn: "span 2" }}>
                <div className="md-card">
                  <div className="md-card-header">
                    <Activity style={{ width: 16, height: 16, color: "#22d3ee" }} />
                    <p className="md-card-title">{t ? t("volumeTrend7Days") : "Volume Trend (7 Days)"}</p>
                  </div>
                  <div className="md-card-content">
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="mddepG" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.25} />
                              <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="mdwitG" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,58,95,0.6)" />
                          <XAxis dataKey="date" stroke="#1e3a5f" fontSize={11} tick={{ fill: "#4d6e90" }} />
                          <YAxis stroke="#1e3a5f" fontSize={11} tick={{ fill: "#4d6e90" }} tickFormatter={(v) => `${v}K`} />
                          <Tooltip content={<DarkTooltip />} />
                          <Area type="monotone" dataKey="deposits" name="Deposits" stroke="#22d3ee" strokeWidth={2} fill="url(#mddepG)" />
                          <Area type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#10b981" strokeWidth={2} fill="url(#mdwitG)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              <div className="md-card">
                <div className="md-card-header">
                  <Building2 style={{ width: 16, height: 16, color: "#22d3ee" }} />
                  <p className="md-card-title">{t ? t("merchantInfo") : "Merchant Info"}</p>
                </div>
                <div className="md-card-content">
                  {[
                    { label: t ? t("settlementPeriod") : "Settlement Period", value: merchant?.settlementPeriod || "N/A" },
                    { label: t ? t("depositFee") : "Deposit Fee", value: merchant?.depositFee || "N/A" },
                    { label: t ? t("withdrawalFee") : "Withdrawal Fee", value: merchant?.withdrawalFee || "N/A" },
                    { label: t ? t("rateLimit") : "Rate Limit", value: merchant?.rateLimit ? `${merchant.rateLimit} req/min` : "N/A" },
                  ].map((row, i) => (
                    <div key={i} className="md-info-row">
                      <p className="md-info-label">{row.label}</p>
                      <p className="md-info-value">{row.value}</p>
                    </div>
                  ))}
                  <div className="md-info-row">
                    <p className="md-info-label">{t ? t("features") : "Features"}</p>
                    <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                      {merchant?.depositEnabled != null && <span className="md-badge-cyan">{t ? t("deposit") : "Deposit"}</span>}
                      {merchant?.withdrawalEnabled != null && <span className="md-badge-cyan">{t ? t("withdrawal") : "Withdrawal"}</span>}
                    </div>
                  </div>
                  <div className="md-info-row">
                    <p className="md-info-label">{t ? t("limits") : "Limits"}</p>
                    <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 2px" }}>
                      Deposit: {merchant?.minDeposit != null && merchant?.maxDeposit != null ? `฿${merchant.minDeposit.toLocaleString()} – ฿${merchant.maxDeposit.toLocaleString()}` : "N/A"}
                    </p>
                    <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                      Withdrawal: {merchant?.minWithdrawal != null && merchant?.maxWithdrawal != null ? `฿${merchant.minWithdrawal.toLocaleString()} – ฿${merchant.maxWithdrawal.toLocaleString()}` : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ gridColumn: "span 3" }}>
                <div className="md-card">
                  <div className="md-card-header">
                    <Activity style={{ width: 16, height: 16, color: "#22d3ee" }} />
                    <p className="md-card-title">{t ? t("transactions") : "Transactions"}</p>
                  </div>
                  <div className="md-card-content">
                    <AdminTransactionTable transactions={combinedData} isLoading={isLoadingQRAndWithdrawals} onView={setSelectedTx} showMerchant={false} showReseller={false} />
                    {totalCombinedPages > 1 && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
                        <p style={{ fontSize: 12, color: "#4d6e90" }}>
                          Showing {overviewPage * pageSize + 1}–{Math.min((overviewPage + 1) * pageSize, combinedQRCodeAndWitData?.total || 0)} of {combinedQRCodeAndWitData?.total || 0}
                        </p>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button className="md-page-btn" onClick={() => setOverviewPage(overviewPage - 1)} disabled={overviewPage === 0}>
                            <ChevronLeft style={{ width: 15, height: 15 }} />
                          </button>
                          <span style={{ fontSize: 12, color: "#64748b" }}>
                            Page {overviewPage + 1} / {totalCombinedPages}
                          </span>
                          <button className="md-page-btn" onClick={() => setOverviewPage(overviewPage + 1)} disabled={overviewPage >= totalCombinedPages - 1}>
                            <ChevronRight style={{ width: 15, height: 15 }} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── DEPOSITS ── */}
          {activeTab === "deposits" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <AdminTransactionTable transactions={depositTransactions} isLoading={qrLoading} onView={setSelectedTx} showMerchant={false} showReseller={false} />
              {depositsTotalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 12, color: "#4d6e90" }}>Showing {depositsPage * pageSize + 1}–{Math.min((depositsPage + 1) * pageSize, depositsTotalTransactions)} of {depositsTotalTransactions}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="md-page-btn" onClick={() => setDepositsPage(depositsPage - 1)} disabled={depositsPage === 0}><ChevronLeft style={{ width: 15, height: 15 }} /></button>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Page {depositsPage + 1} / {depositsTotalPages}</span>
                    <button className="md-page-btn" onClick={() => setDepositsPage(depositsPage + 1)} disabled={depositsPage >= depositsTotalPages - 1}><ChevronRight style={{ width: 15, height: 15 }} /></button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── WITHDRAWALS ── */}
          {activeTab === "withdrawals" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <AdminTransactionTable transactions={withdrawalTransactions} isLoading={withdrawalsLoadingPaginated} onView={setSelectedTx} showMerchant={false} showReseller={false} />
              {withdrawalsTotalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 12, color: "#4d6e90" }}>Showing {withdrawalsPage * pageSize + 1}–{Math.min((withdrawalsPage + 1) * pageSize, withdrawalsTotalTransactions)} of {withdrawalsTotalTransactions}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="md-page-btn" onClick={() => setWithdrawalsPage(withdrawalsPage - 1)} disabled={withdrawalsPage === 0}><ChevronLeft style={{ width: 15, height: 15 }} /></button>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Page {withdrawalsPage + 1} / {withdrawalsTotalPages}</span>
                    <button className="md-page-btn" onClick={() => setWithdrawalsPage(withdrawalsPage + 1)} disabled={withdrawalsPage >= withdrawalsTotalPages - 1}><ChevronRight style={{ width: 15, height: 15 }} /></button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SETTINGS ── */}
          {activeTab === "settings" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* API Keys */}
              <div className="md-card">
                <div className="md-card-header">
                  <Key style={{ width: 16, height: 16, color: "#22d3ee" }} />
                  <p className="md-card-title">{t ? t("apiCredentialsTitle") : "API Credentials"}</p>
                </div>
                <div className="md-card-content" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {[
                    { label: t ? t("apiKeyPublic") : "API Key", show: showApiKey, toggle: () => setShowApiKey(!showApiKey), value: merchant?.apiKey, copyType: "api" },
                    { label: t ? t("secretKey") : "Secret Key", show: showSecretKey, toggle: () => setShowSecretKey(!showSecretKey), value: merchant?.secretKey, copyType: "secret" },
                  ].map((item, i) => (
                    <div key={i}>
                      <p style={{ fontSize: 11, color: "#4d6e90", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.label}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <code className="md-code" style={{ flex: 1 }}>{item.show ? (item.value || "Not set") : "••••••••••••••••••••••••"}</code>
                        <button className="md-icon-btn" onClick={item.toggle}>
                          {item.show ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                        </button>
                        <button className="md-icon-btn" onClick={() => item.value && copyKey(item.value, item.copyType)}>
                          {copiedKey === item.copyType ? <Check style={{ width: 14, height: 14, color: "#10b981" }} /> : <Copy style={{ width: 14, height: 14 }} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Webhook */}
              <div className="md-card">
                <div className="md-card-header">
                  <Webhook style={{ width: 16, height: 16, color: "#22d3ee" }} />
                  <p className="md-card-title">{t ? t("webhookInfo") : "Webhook Configuration"}</p>
                </div>
                <div className="md-card-content" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {[
                    { label: t ? t("callbackUrlLabel") : "Callback URL", value: merchant?.callbackUrl || "Not configured" },
                    { label: t ? t("webhookUrl") : "Webhook URL", value: merchant?.webhookUrl || "Not configured" },
                  ].map((item, i) => (
                    <div key={i}>
                      <p style={{ fontSize: 11, color: "#4d6e90", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.label}</p>
                      <code className="md-code">{item.value}</code>
                    </div>
                  ))}
                  <div>
                    <p style={{ fontSize: 11, color: "#4d6e90", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t ? t("ipWhitelist") : "IP Whitelist"}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {(merchant?.ip_whitelist || []).length > 0
                        ? merchant.ip_whitelist.map((ip, i) => <span key={i} className="md-badge-cyan">{ip}</span>)
                        : <span style={{ fontSize: 13, color: "#334155" }}>No IPs whitelisted</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} merchant={merchant} isQRCode={!!selectedTx?.is_qr_code} />
    </div>
  );
}